import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useBulkSendDocuments } from "../../../hooks/api/projectDocumentsApiHooks";

/**
 * Modal para envio em lote de documentos via WhatsApp (automaticamente ao integrador)
 * @param {object} props
 * @param {boolean} props.isOpen - Se o modal está aberto
 * @param {function} props.onClose - Callback ao fechar
 * @param {number} props.projectId - ID do projeto
 * @param {object} props.project - Dados do projeto (para pegar o integrador)
 * @param {array} props.availableDocuments - Documentos disponíveis para envio
 * @param {function} props.onSuccess - Callback ao sucesso
 */
export default function BulkSendDocumentsModal({
  isOpen,
  onClose,
  projectId,
  project,
  availableDocuments,
  onSuccess,
}) {
  const [selectedDocuments, setSelectedDocuments] = useState(
    availableDocuments?.map((doc) => doc.id) || []
  );
  const [sendResult, setSendResult] = useState(null);

  const { mutateAsync: bulkSend, isLoading } = useBulkSendDocuments();

  // Buscar dados do integrador do projeto
  const integradorName = project?.clients?.name || "Integrador";
  const integradorPhone = project?.clients?.phone

  // Toggle de seleção de documento
  const handleToggleDocument = (documentId) => {
    setSelectedDocuments((prev) => {
      if (prev.includes(documentId)) {
        return prev.filter((id) => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  // Toggle selecionar todos
  const handleToggleAll = () => {
    if (selectedDocuments.length === availableDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(availableDocuments.map((doc) => doc.id));
    }
  };

  // Handler de envio
  const handleSend = async () => {
    if (!integradorPhone) {
      toast.error("Integrador não possui telefone cadastrado no projeto");
      return;
    }

    if (selectedDocuments.length === 0) {
      toast.error("Selecione pelo menos um documento");
      return;
    }

    try {
      const result = await bulkSend({
        projectId,
        documentIds: selectedDocuments,
        recipientPhone: integradorPhone,
        recipientName: integradorName,
      });

      setSendResult(result);

      // Mostrar mensagem de sucesso
      if (result.successCount > 0) {
        toast.success(`${result.successCount} documento(s) enviado(s) com sucesso!`);
      }
      if (result.failureCount > 0) {
        toast.warning(`${result.failureCount} documento(s) falharam no envio`);
      }
    } catch (error) {
      console.error("Erro ao enviar documentos:", error);
      const errorMessage =
        error.response?.data?.message || "Erro ao enviar documentos via WhatsApp";
      toast.error(errorMessage);
    }
  };

  // Fechar e resetar
  const handleClose = () => {
    if (sendResult && sendResult.successCount > 0 && onSuccess) {
      onSuccess();
    }
    setSendResult(null);
    onClose();
  };

  // Se já tem resultado, mostrar relatório
  if (sendResult) {
    return (
      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Resultado do Envio
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Resumo */}
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Total: {sendResult.totalDocuments} documento(s)
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
              <Box>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                  {sendResult.successCount}
                </Typography>
                <Typography variant="caption">✅ Sucesso</Typography>
              </Box>
              <Box>
                <Typography variant="h4" color="error.main" sx={{ fontWeight: 700 }}>
                  {sendResult.failureCount}
                </Typography>
                <Typography variant="caption">❌ Falha</Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Lista de resultados */}
          {sendResult.results?.map((result, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                mb: 1,
                bgcolor: result.success ? "success.lighter" : "error.lighter",
                borderRadius: 1,
              }}
            >
              {result.success ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : (
                <ErrorIcon color="error" fontSize="small" />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {result.documentName || "Documento"}
                </Typography>
                {!result.success && result.error && (
                  <Typography variant="caption" color="error">
                    Erro: {result.error}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleClose}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Modal de envio normal
  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WhatsAppIcon color="success" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Envio em Lote via WhatsApp
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Informações do Destinatário */}
        {integradorPhone ? (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: "success.lighter",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "success.main",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              📱 Destinatário: {integradorName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Telefone: {integradorPhone}
            </Typography>
          </Box>
        ) : (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              ⚠️ Integrador não possui telefone cadastrado no projeto.
            </Typography>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Seleção de Documentos */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Documentos Disponíveis:
            </Typography>
            <Button size="small" onClick={handleToggleAll} disabled={isLoading}>
              {selectedDocuments.length === availableDocuments.length
                ? "Desmarcar Todos"
                : "Selecionar Todos"}
            </Button>
          </Box>

          <Box
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1,
            }}
          >
            {availableDocuments?.map((document) => (
              <Box key={document.id} sx={{ mb: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedDocuments.includes(document.id)}
                      onChange={() => handleToggleDocument(document.id)}
                      disabled={isLoading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {document.documentType?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {document.fileName}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Selecionados: {selectedDocuments.length} de {availableDocuments?.length || 0}
          </Typography>
        </Box>

        {/* Progress (durante envio) */}
        {isLoading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" sx={{ mt: 0.5, display: "block", textAlign: "center" }}>
              Enviando documentos...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSend}
          disabled={
            !integradorPhone ||
            selectedDocuments.length === 0 ||
            isLoading
          }
          startIcon={<WhatsAppIcon />}
        >
          {isLoading ? "Enviando..." : `Enviar Selecionados (${selectedDocuments.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
